import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApiError,
  Badge,
  Button,
  Modal,
  Pagination,
  Select,
  Spinner,
  useToast,
} from "@toss-ads/ui";
import {
  listAuditEntries,
  revertAuditEntry,
  type AuditEntry,
} from "../../lib/api";
import { useUrlState } from "../../lib/use-url-state";

const PER_PAGE = 20;

const ENTITY_LABELS: Record<string, string> = {
  advertiser: "광고주",
  admin: "어드민",
  external_source: "소스",
  platform: "매체",
  kakao_account: "카카오 계정",
  kakao_form: "카카오 폼",
  user: "유저",
  user_advertisers: "유저-광고주",
  lead: "리드",
};

const ACTION_LABELS: Record<string, string> = {
  create: "생성",
  update: "수정",
  delete: "삭제",
  revert: "되돌림",
};

function actionVariant(a: string): "primary" | "neutral" | "danger" {
  if (a === "delete") return "danger";
  if (a === "create") return "primary";
  return "neutral";
}

export function AuditLogPage() {
  const [entityType, setEntityType] = useUrlState("type", "");
  const [pageStr, setPageStr] = useUrlState("page", "1");
  const page = Number(pageStr) || 1;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin", "audit", entityType, page],
    queryFn: () =>
      listAuditEntries({
        limit: PER_PAGE,
        offset: (page - 1) * PER_PAGE,
        entityType: entityType || undefined,
      }),
  });

  const total = data?.total ?? 0;

  return (
    <div className="max-w-[1100px] mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-[18px] font-semibold tracking-tight">
          변경 기록
          <span className="ml-2 text-[12px] text-ink-subtle tabular-nums">
            ({total.toLocaleString()})
          </span>
        </h1>
        <Select
          value={entityType}
          onChange={(e) => {
            setEntityType(e.target.value);
            setPageStr("1");
          }}
        >
          <option value="">전체 대상</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </div>
      <p className="text-[12px] text-ink-subtle mb-3 -mt-1">
        admin/developer 의 설정·카카오 변경 이력. 수정·삭제는 되돌릴 수 있습니다.
      </p>

      <div className="rounded-lg border bg-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted">
            <tr>
              <Th width={150}>시각</Th>
              <Th width={180}>행위자</Th>
              <Th width={70}>동작</Th>
              <Th width={90}>대상</Th>
              <Th>요약</Th>
              <Th width={90}>상태</Th>
              <Th width={72}>관리</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="py-16">
                  <div className="flex justify-center text-ink-subtle">
                    <Spinner size={20} />
                  </div>
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td
                  colSpan={7}
                  className="py-12 text-center text-[13px] text-danger"
                >
                  {error instanceof ApiError
                    ? error.message
                    : "데이터를 불러오지 못했습니다."}
                </td>
              </tr>
            )}
            {!isLoading && !isError && total === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="py-12 text-center text-[13px] text-ink-subtle"
                >
                  기록이 없습니다.
                </td>
              </tr>
            )}
            {data?.items.map((e) => (
              <AuditRow key={e.id} entry={e} />
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center mt-4">
        <Pagination
          total={total}
          perPage={PER_PAGE}
          current={page}
          onChange={(n) => setPageStr(String(n))}
        />
      </div>
    </div>
  );
}

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = React.useState(false);
  const [confirm, setConfirm] = React.useState(false);
  const qc = useQueryClient();
  const toast = useToast();

  const revert = useMutation({
    mutationFn: () => revertAuditEntry(entry.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "audit"] });
      // 되돌린 대상 목록도 갱신.
      qc.invalidateQueries({ queryKey: ["admin", "advertisers"] });
      qc.invalidateQueries({ queryKey: ["admin", "admins"] });
      qc.invalidateQueries({ queryKey: ["admin", "external-sources"] });
      qc.invalidateQueries({ queryKey: ["admin", "platforms"] });
      qc.invalidateQueries({ queryKey: ["admin", "kakao"] });
      toast.success("되돌렸습니다");
      setConfirm(false);
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "되돌리기 실패"),
  });

  return (
    <>
      <tr className="border-t border-border align-middle">
        <Td>
          <span className="text-[12px] text-ink-muted tabular-nums">
            {formatKst(entry.createdAt)}
          </span>
        </Td>
        <Td>
          <span className="text-[12px]">{entry.actorEmail ?? "—"}</span>
        </Td>
        <Td>
          <Badge variant={actionVariant(entry.action)}>
            {ACTION_LABELS[entry.action] ?? entry.action}
          </Badge>
        </Td>
        <Td>
          <span className="text-[12px]">
            {ENTITY_LABELS[entry.entityType] ?? entry.entityType}
          </span>
        </Td>
        <Td>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[12px] text-left text-ink hover:underline"
          >
            {entry.summary ?? "(요약 없음)"}
          </button>
        </Td>
        <Td>
          {entry.revertedAt ? (
            <span className="text-[11px] text-ink-subtle">되돌림</span>
          ) : (
            <span className="text-[11px] text-ink-subtle">—</span>
          )}
        </Td>
        <Td>
          {entry.revertible && (
            <button
              type="button"
              onClick={() => setConfirm(true)}
              className="text-[12px] text-primary hover:underline"
            >
              되돌리기
            </button>
          )}
        </Td>
      </tr>
      {open && (
        <tr className="border-t border-border bg-surface-muted/40">
          <td colSpan={7} className="px-3 py-3">
            <DiffView entry={entry} />
          </td>
        </tr>
      )}

      <Modal
        open={confirm}
        onClose={() => setConfirm(false)}
        title="변경 되돌리기"
        description="이 변경을 되돌립니다. 되돌리기도 기록으로 남습니다."
      >
        <div className="text-[13px] text-ink-muted">
          {ENTITY_LABELS[entry.entityType] ?? entry.entityType} ·{" "}
          {ACTION_LABELS[entry.action] ?? entry.action} — {entry.summary}
        </div>
        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-border">
          <Button variant="text" size="md" onClick={() => setConfirm(false)}>
            취소
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={revert.isPending}
            onClick={() => revert.mutate()}
          >
            되돌리기
          </Button>
        </div>
      </Modal>
    </>
  );
}

function DiffView({ entry }: { entry: AuditEntry }) {
  const before = asRecord(entry.before);
  const after = asRecord(entry.after);
  if (entry.action === "update" && before && after) {
    const keys = Object.keys({ ...before, ...after }).filter(
      (k) => stringify(before[k]) !== stringify(after[k]),
    );
    if (keys.length === 0)
      return <p className="text-[12px] text-ink-subtle">변경된 필드 없음</p>;
    return (
      <div className="space-y-1.5">
        {keys.map((k) => (
          <div key={k} className="text-[12px] flex gap-2 flex-wrap">
            <span className="text-ink-subtle w-40 shrink-0">{k}</span>
            <span className="text-danger line-through break-all">
              {stringify(before[k])}
            </span>
            <span className="text-ink-subtle">→</span>
            <span className="text-success break-all">
              {stringify(after[k])}
            </span>
          </div>
        ))}
      </div>
    );
  }
  const obj = entry.action === "delete" ? before : after;
  if (!obj) return <p className="text-[12px] text-ink-subtle">내용 없음</p>;
  return (
    <div className="space-y-1">
      {Object.entries(obj).map(([k, v]) => (
        <div key={k} className="text-[12px] flex gap-2">
          <span className="text-ink-subtle w-40 shrink-0">{k}</span>
          <span className="text-ink break-all">{stringify(v)}</span>
        </div>
      ))}
    </div>
  );
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function stringify(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function formatKst(iso: string): string {
  const d = new Date(iso);
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${kst.getUTCFullYear()}-${p(kst.getUTCMonth() + 1)}-${p(kst.getUTCDate())} ${p(kst.getUTCHours())}:${p(kst.getUTCMinutes())}`;
}

function Th({
  children,
  width,
}: {
  children?: React.ReactNode;
  width?: number;
}) {
  return (
    <th
      className="px-2.5 py-2 text-left text-[12px] font-medium text-ink-subtle"
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children?: React.ReactNode }) {
  return <td className="px-2.5 py-3 align-middle">{children}</td>;
}
